import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ButtonText from "components/common/buttonText";
import ButtonSlider from "components/common/buttonSlider";
import ToggleText from "components/common/toggleText";
import InputDate from "components/common/inputDate";
import InputNumber from "components/common/inputNumber";
import InputTime from "components/common/inputTime";
import DropdownSelector from "components/common/dropdownSelector/dropdownSelector";
import InputText from "components/common/inputText/inputText";
import Alert from "components/common/alert";
import BackButton from "components/dashboard/backButton";
import WidgetLoadingSkeleton from "components/dashboard/widgetLoadingSkeleton";
import { getToken } from "context/auth";
import { getAvailableParkingSpots, bulkCreateMarketplacePermits } from "api/permits";
import { getCommunitiesForParkingArea } from "api/communities";
import { useMessages } from "context/messages";
import { useDashboard } from "context/dashboard";
import SelectParkingSpotsModal from "components/dashboard/selectParkingSpotsModal";
import CommunitiesModal from "pages/communities/communitiesModal";

// Inline-komponent (matchar permit.js — ej extraherad till komponent-bibliotek än).
const ButtonWeekday = ({ title, isSelected, onClick }) => {
  const className = isSelected
    ? "flex h-12 w-12 rounded-lg bg-airpark-green hover:bg-airpark-green-400 items-center justify-center text-white"
    : "flex h-12 w-12 rounded-lg border-[1px] border-airpark-gray-400 items-center justify-center hover:border-airpark-gray-500";
  return (
    <button className={className} onClick={onClick}>
      <span>{title}</span>
    </button>
  );
};

const BulkCreatePermitsPage = () => {
  const { t } = useTranslation();
  const { showToast, showBetweenPagesMessage } = useMessages();
  const navigate = useNavigate();
  const { organizationExtId, parkingAreaExtId } = useParams();
  const { organization } = useDashboard();

  const [spots, setSpots] = useState([]);
  const [selectedSpotExtIds, setSelectedSpotExtIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  // Settings
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("Month");
  const [startDate, setStartDate] = useState(new Date());
  const [isOpenEnded, setIsOpenEnded] = useState(true);
  const [endDate, setEndDate] = useState(new Date());
  const [requiresContractCode, setRequiresContractCode] = useState(false);
  const [isCommunity, setIsCommunity] = useState(false);
  const [isSublettingAllowed, setIsSublettingAllowed] = useState(false);
  const [isPaymentHandledManually, setIsPaymentHandledManually] = useState(false);
  const [description, setDescription] = useState("");
  // Paritet med "Skapa ett" — default true (publicera om vid avslut)
  const [reAddToMarketplace, setReAddToMarketplace] = useState(true);
  // Veckodagar + tid — backend stöder fälten men frontend skickade default-värden hardkodat
  const [monday, setMonday] = useState(true);
  const [tuesday, setTuesday] = useState(true);
  const [wednesday, setWednesday] = useState(true);
  const [thursday, setThursday] = useState(true);
  const [friday, setFriday] = useState(true);
  const [saturday, setSaturday] = useState(true);
  const [sunday, setSunday] = useState(true);
  const [timeFrom, setTimeFrom] = useState("00:00:00");
  const [timeTo, setTimeTo] = useState("23:59:00");
  // 2.10 A3: community-mottagare i Skapa flera (matchar Skapa ett)
  const [addUserToCommunities, setAddUserToCommunities] = useState(false);
  const [selectedCommunityExtIds, setSelectedCommunityExtIds] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [displayCommunitiesModal, setDisplayCommunitiesModal] = useState(false);
  // 2.10 A4: använd samma SelectParkingSpotsModal som "Skapa ett"
  const [displayParkingSpotsModal, setDisplayParkingSpotsModal] = useState(false);
  const [selectedParkingSpots, setSelectedParkingSpots] = useState([]);

  // Håll selectedSpotExtIds synkad med selectedParkingSpots (modalen arbetar med objekt)
  useEffect(() => {
    const ids = selectedParkingSpots.map(p => p.parkingSpotExtId);
    setSelectedSpotExtIds(ids);
  }, [selectedParkingSpots]);

  useEffect(() => {
    loadSpots();
  }, [parkingAreaExtId]);

  const loadSpots = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const [spotsResult, communitiesResult] = await Promise.all([
        getAvailableParkingSpots(token, parkingAreaExtId),
        getCommunitiesForParkingArea(token, parkingAreaExtId).catch(() => []),
      ]);
      setSpots(spotsResult || []);
      setCommunities(Array.isArray(communitiesResult) ? communitiesResult : (communitiesResult?.communities || []));
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const getSpotLabel = (spot) => {
    const name = spot.isNumbered ? spot.spotIdentifier : t("createPermitPage.notNumbered");
    return `${spot.parkingSpotExtId} - ${name}`;
  };

  const filteredSpots = spots.filter((s) =>
    !searchText || getSpotLabel(s).toLowerCase().includes(searchText.toLowerCase())
  );

  const toggleSpot = (extId) => {
    setSelectedSpotExtIds((prev) =>
      prev.includes(extId) ? prev.filter((id) => id !== extId) : [...prev, extId]
    );
  };

  const selectAll = () => setSelectedSpotExtIds(filteredSpots.map((s) => s.parkingSpotExtId));
  const deselectAll = () => setSelectedSpotExtIds([]);

  const handleCreate = async () => {
    if (selectedSpotExtIds.length === 0) {
      showToast(t("generic.errorTitle"), t("bulkCreateModal.noSpotsSelected"), "error");
      return;
    }
    if (!isPaymentHandledManually && (price === "" || isNaN(Number(price)))) {
      showToast(t("generic.errorTitle"), t("bulkCreateModal.priceRequired"), "error");
      return;
    }

    setIsCreating(true);
    try {
      const token = await getToken();
      const result = await bulkCreateMarketplacePermits(token, {
        parkingAreaExtId,
        parkingSpotExtIds: selectedSpotExtIds,
        description,
        price: price !== "" && !isNaN(Number(price)) ? Number(price) : null,
        unit,
        startDateTime: `${format(startDate, "yyyy-MM-dd")}T${format(startDate, "HH:mm")}`,
        endDateTime: isOpenEnded ? null : `${format(endDate, "yyyy-MM-dd")}T${format(endDate, "HH:mm")}`,
        isMarketplace: true,
        requiresContractCode,
        isCommunity,
        includeUserInParkingAreaCommunities: addUserToCommunities,
        communityExtIds: addUserToCommunities ? selectedCommunityExtIds : null,
        isSublettingAllowed,
        isPaymentHandledManually,
        reAddToMarketplace,
        monday, tuesday, wednesday, thursday, friday, saturday, sunday,
        timeFrom, timeTo,
      });

      showBetweenPagesMessage(
        t("generic.successTitle"),
        t("bulkCreateModal.created", { count: result.permitsCreated }),
        () => { navigate(`/${organizationExtId.toLowerCase()}/parking-areas/${parkingAreaExtId}/permits/marketplace`); },
        "success"
      );
    } catch (e) {
      console.error(e);
      showToast(t("generic.errorTitle"), t("generic.errorDescription"), "error");
    } finally {
      setIsCreating(false);
    }
  };

  const unitOptions = [
    { title: `kr/${t("priceUnit.month")}`, value: "Month" },
    { title: `kr/${t("priceUnit.day")}`, value: "Day" },
    { title: `kr/${t("priceUnit.hour")}`, value: "Hour" },
  ];

  return (
    <>
      <BackButton />
      <div className="flex flex-col m-auto max-w-[448px] w-full">
        {isLoading ? (
          <WidgetLoadingSkeleton />
        ) : (
          <>
            <h1 className="mt-14 text-center text-2xl font-medium mb-8">
              {t("bulkCreateModal.title")}
            </h1>

            {/* Anteckning */}
            <div className="mb-3">
              <InputText
                title={t("createPermitPage.description")}
                value={description}
                onChange={setDescription}
                placeHolder={t("createPermitPage.descriptionPlaceholder")}
              />
            </div>

            {/* 2.10 A4: Spotval via SelectParkingSpotsModal (matchar "Skapa ett") */}
            <div className="flex flex-col mb-3 gap-3">
              <div className="flex justify-between items-center">
                <span className="text-base font-medium">{t("bulkCreateModal.selectSpots")}</span>
                <ButtonText secondary onClick={() => setDisplayParkingSpotsModal(true)}>
                  {t("bulkCreateModal.selectSpots")}
                </ButtonText>
              </div>

              {selectedParkingSpots.length > 0 && (
                <Alert
                  template="info"
                  title={t("bulkCreateModal.selectSpots")}
                  description={t("bulkCreateModal.spotsSelected", { count: selectedParkingSpots.length })}
                />
              )}
              {selectedParkingSpots.length === 0 && (
                <div className="text-airpark-gray-500 text-sm p-2">{t("bulkCreateModal.noSpotsSelected")}</div>
              )}
            </div>

            {/* Period */}
            <div className="flex flex-col mb-3 gap-3">
              <InputDate
                title={t("bulkCreateModal.startDate")}
                value={startDate}
                onChange={setStartDate}
                className="w-full"
              />

              {/* Veckodagar — paritet med "Skapa ett" */}
              <div className="flex flex-col">
                <span className="font-medium pb-1">{t("createPermitPage.onWeekdays")}</span>
                <div className="flex justify-between">
                  <ButtonWeekday title={t("dayShortNames.monday")} isSelected={monday} onClick={() => setMonday(!monday)} />
                  <ButtonWeekday title={t("dayShortNames.tuesday")} isSelected={tuesday} onClick={() => setTuesday(!tuesday)} />
                  <ButtonWeekday title={t("dayShortNames.wednesday")} isSelected={wednesday} onClick={() => setWednesday(!wednesday)} />
                  <ButtonWeekday title={t("dayShortNames.thursday")} isSelected={thursday} onClick={() => setThursday(!thursday)} />
                  <ButtonWeekday title={t("dayShortNames.friday")} isSelected={friday} onClick={() => setFriday(!friday)} />
                  <ButtonWeekday title={t("dayShortNames.saturday")} isSelected={saturday} onClick={() => setSaturday(!saturday)} />
                  <ButtonWeekday title={t("dayShortNames.sunday")} isSelected={sunday} onClick={() => setSunday(!sunday)} />
                </div>
              </div>

              {/* Tid */}
              <div className="flex flex-col">
                <span className="font-medium pb-1">{t("createPermitPage.between")}</span>
                <div className="flex md:flex-row flex-col">
                  <InputTime value={timeFrom} className="w-full md:mr-1" onChange={(value) => setTimeFrom(value + ":00")} />
                  <InputTime value={timeTo} className="w-full md:ml-1" onChange={(value) => setTimeTo(value + ":00")} />
                </div>
              </div>

              <ButtonSlider
                title={t("createPermitPage.noEndDate")}
                isToggled={isOpenEnded}
                onClick={() => setIsOpenEnded(!isOpenEnded)}
              />

              {!isOpenEnded && (
                <InputDate
                  title={t("bulkCreateModal.endDate")}
                  value={endDate}
                  onChange={setEndDate}
                  className="w-full"
                />
              )}
            </div>

            <hr className="w-full mt-4 mb-6 border-airpark-gray-400" />

            {/* Marketplace-inställningar */}
            <div className="flex flex-col mb-3">
              <DropdownSelector
                title={t("createPermitPage.marketplaceAvailability")}
                options={[
                  { title: t("createPermitPage.everyone"), value: "everyone" },
                  { title: t("createPermitPage.community"), value: "community" },
                ]}
                defaultValue={isCommunity ? "community" : "everyone"}
                onChange={(value) => setIsCommunity(value === "community")}
              />
            </div>

            {/* Lägg upp på marketplace igen — paritet med "Skapa ett" */}
            <div className="flex flex-col mb-6">
              <ButtonSlider
                title={t("createPermitPage.reAddToMarketplace")}
                isToggled={reAddToMarketplace}
                onClick={() => setReAddToMarketplace(!reAddToMarketplace)}
                tooltip={t("createPermitPage.reAddToMarketplaceTooltip")}
              />
            </div>

            <div className="flex flex-col mb-6">
              <ButtonSlider
                title={t("createPermitPage.requiresContractCode")}
                isToggled={requiresContractCode}
                onClick={() => setRequiresContractCode(!requiresContractCode)}
              />
            </div>

            <hr className="w-full mb-6 border-airpark-gray-400" />

            {/* Andrahand */}
            <div className="flex flex-col mb-3">
              <ButtonSlider
                title={t("createPermitPage.isSublettingAllowed")}
                isToggled={isSublettingAllowed}
                onClick={() => setIsSublettingAllowed(!isSublettingAllowed)}
              />
            </div>

            <hr className="w-full mt-6 mb-6 border-airpark-gray-400" />

            {/* Fakturering */}
            <div className="flex flex-col mb-6 gap-3">
              <ToggleText
                title={t("createPermitPage.invoicedBy")}
                defaultText={t("generic.airPark")}
                toggledText={t("createPermitPage.externally")}
                isToggled={isPaymentHandledManually}
                onClick={() => setIsPaymentHandledManually(!isPaymentHandledManually)}
              />

              {!isPaymentHandledManually && (
                <>
                  <DropdownSelector
                    title={t("createPermitPage.price")}
                    options={unitOptions}
                    defaultValue={unit}
                    onChange={(v) => setUnit(v)}
                  />
                  <InputNumber
                    value={price}
                    unit={`kr/${t(`priceUnit.${unit.toLowerCase()}`)}`}
                    onChange={setPrice}
                    min={0}
                  />
                </>
              )}

              {isPaymentHandledManually && (
                <Alert
                  title={t("createPermitPage.invoicedExternallyTitle")}
                  description={t("createPermitPage.invoicedExternallyDescription")}
                  template="info"
                />
              )}
            </div>

            {/* Community-mottagare — modal-väljare, paritet med "Skapa ett" */}
            <div className="flex flex-col mb-6 gap-3">
              <ButtonSlider
                title={t("createPermitPage.addUserToCommunity")}
                isToggled={addUserToCommunities}
                onClick={() => {
                  const toggled = !addUserToCommunities;
                  setAddUserToCommunities(toggled);
                  if (!toggled) {
                    setSelectedCommunityExtIds([]);
                  } else if ((selectedCommunityExtIds || []).length === 0) {
                    setDisplayCommunitiesModal(true);
                  }
                }}
                tooltip={t("createPermitPage.addUserToCommunityTooltip")}
              />
              {addUserToCommunities && communities?.length === 0 && (
                <Alert
                  template="warning"
                  title={t("createPermitPage.noCommunitiesFoundTitle")}
                  description={t("createPermitPage.noCommunitiesFoundDescription")}
                />
              )}
              {addUserToCommunities && communities?.length > 0 && (
                <div className="flex flex-col gap-3">
                  <Alert
                    template="info"
                    title={t("createPermitPage.userWillBeAddedToCommunitiesTitle")}
                    description={
                      (selectedCommunityExtIds || []).length > 0
                        ? t("createPermitPage.userWillBeAddedToCommunitiesDescription", {
                            communities: (communities || [])
                              .filter((c) => (selectedCommunityExtIds || []).includes(c.communityExtId))
                              .map((c) => c.name)
                              .join(", "),
                          })
                        : t("createPermitPage.pleaseChooseCommunities")
                    }
                  />
                  <ButtonText className="w-full" onClick={() => setDisplayCommunitiesModal(true)} isSecondary>
                    {(selectedCommunityExtIds || []).length > 0 ? t("generic.change") : t("generic.choose")}
                  </ButtonText>
                </div>
              )}
            </div>

            <ButtonText
              isLoading={isCreating}
              onClick={handleCreate}
              disabled={isCreating || selectedSpotExtIds.length === 0}
            >
              {t("bulkCreateModal.createButton", { count: selectedSpotExtIds.length })}
            </ButtonText>

            {/* 2.10 A4: samma modal som "Skapa ett" */}
            <SelectParkingSpotsModal
              display={displayParkingSpotsModal}
              setDisplay={setDisplayParkingSpotsModal}
              parkingSpots={spots}
              selectedParkingSpots={selectedParkingSpots}
              setSelectedParkingSpots={setSelectedParkingSpots}
              isSingleSelectionOnly={false}
            />

            <CommunitiesModal
              display={displayCommunitiesModal}
              setDisplay={setDisplayCommunitiesModal}
              communities={communities || []}
              selectedCommunities={selectedCommunityExtIds || []}
              setSelectedCommunities={(ids) => setSelectedCommunityExtIds(ids || [])}
              isSingleSelectionOnly={false}
            />
          </>
        )}
      </div>
    </>
  );
};

export default BulkCreatePermitsPage;
